/**
 * InfoTooltip — small ⓘ icon that shows an educational popover on click.
 *
 * Usage:
 *   <InfoTooltip content="Les ATS filtrent 75% des CV avant lecture humaine." />
 */

import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InfoTooltipProps {
  content: string;
  /** Default: bottom-center above the icon */
  placement?: 'top' | 'bottom';
  className?: string;
}

export function InfoTooltip({ content, placement = 'top', className }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className={cn('relative inline-flex items-center', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-white/25 hover:text-white/55 transition-colors"
        aria-label="En savoir plus"
      >
        <Info className="size-3.5" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 left-1/2 -translate-x-1/2 w-64 p-3 rounded-xl',
            'bg-[#18182a] border border-white/15 shadow-xl',
            'text-xs text-white/70 leading-relaxed',
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
          )}
        >
          {content}
          {/* Arrow */}
          <span
            className={cn(
              'absolute left-1/2 -translate-x-1/2 w-2 h-2',
              'bg-[#18182a] border-white/15',
              placement === 'top'
                ? 'top-full -mt-1 border-r border-b rotate-45'
                : 'bottom-full -mb-1 border-l border-t rotate-45',
            )}
          />
        </div>
      )}
    </div>
  );
}
